/**
 * Workspace store — the SINGLE active-client context.
 *
 * One stored value, `selection`, describes which client the tab is working on:
 *
 *   - { kind: 'workspace', workspace } — the normal path: the full rich object
 *     (id, name, slug, website, product_package, has_google_ads).
 *   - { kind: 'tenant', tenantId }     — explicit unlinked escape hatch for
 *     deep links whose ?tenant_id= has no matching workspace (Technical Audit,
 *     MCC overview). Represented honestly instead of as a two-store desync.
 *   - null                             — pre-reconciliation only; ProtectedRoute
 *     resolves a selection within the same auth pass.
 *
 * `activeTenantId` (the legacy tenant_id=<website> API key) is DERIVED from
 * the selection via activeTenantIdFromSelection() — it is never stored, so
 * the old workspace↔tenant desync class is unrepresentable. The three
 * workspace→tenant sync paths this file used to own (setter bridge, rehydrate
 * side-effect, cross-store subscription) are deleted; useTenantStore is now a
 * read-only facade over this store.
 *
 * `currentWorkspace` remains as a read-only PROJECTION of the selection for
 * the existing importers that read it. It is written atomically inside the
 * same set() calls that write `selection` (never independently, never via a
 * subscription) and is recomputed from the persisted selection at rehydrate.
 * New code should read `selection` / activeTenantIdFromSelection instead.
 *
 * Persistence: ONE per-tab key ('workspace-storage', v2) via perTabStorage —
 * this tab's sessionStorage first, localStorage mirror for fresh tabs. v1
 * shapes ({ currentWorkspace }) are migrated; a v1 null selection falls back
 * to the legacy 'tenant-storage' key, seeding the unlinked-tenant kind, and
 * the legacy key is then removed from both storage layers.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { fetchWorkspaces } from '../services/authApi';
import type { UserProfile, Workspace } from '../types/auth';
import { perTabStorage } from './perTabStorage';

// ---------------------------------------------------------------------------
// Selection model
// ---------------------------------------------------------------------------

export type ClientSelection =
  | { kind: 'workspace'; workspace: Workspace }
  | { kind: 'tenant'; tenantId: string }
  | null;

/**
 * The tenant_id=<website> API key for the active selection. Derived, never
 * stored: a website-less workspace yields null (no data plane yet), an
 * unlinked tenant yields its domain.
 */
export function activeTenantIdFromSelection(selection: ClientSelection): string | null {
  if (!selection) return null;
  return selection.kind === 'workspace'
    ? (selection.workspace.website ?? null)
    : selection.tenantId;
}

/** Read-only projection of the selection for legacy currentWorkspace readers. */
function projectCurrentWorkspace(selection: ClientSelection): Workspace | null {
  return selection?.kind === 'workspace' ? selection.workspace : null;
}

// ---------------------------------------------------------------------------
// Legacy persistence migration (v1 → v2)
// ---------------------------------------------------------------------------

const LEGACY_TENANT_KEY = 'tenant-storage';

/** Session-first, then the shared mirror — perTabStorage's own read order. */
function readLegacyRaw(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * The legacy tenant store's persisted currentTenant, if any. v1 persisted
 * shapes: { state: { currentTenant: string | null }, version: 0 }.
 */
export function readLegacyTenantWebsite(): string | null {
  const raw = readLegacyRaw(LEGACY_TENANT_KEY);
  if (!raw) return null;
  try {
    const website = (JSON.parse(raw) as { state?: { currentTenant?: unknown } })?.state
      ?.currentTenant;
    return typeof website === 'string' && website.length > 0 ? website : null;
  } catch {
    return null;
  }
}

/**
 * Remove the legacy key from BOTH storage layers. perTabStorage.removeItem
 * deliberately keeps the localStorage mirror, which is right for the live key
 * but would leave the retired one behind — legacy cleanup must be explicit.
 */
export function clearLegacyTenantStorage(): void {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(LEGACY_TENANT_KEY);
    window.localStorage.removeItem(LEGACY_TENANT_KEY);
  } catch {
    /* non-fatal */
  }
}

interface PersistedWorkspaceStateV2 {
  selection: ClientSelection;
  workspaces: Workspace[];
  userProfile: UserProfile | null;
}

/**
 * Rehydration merge: the persisted selection is the source of truth and
 * `currentWorkspace` is recomputed from it — the projection can never come
 * back from storage disagreeing with the selection.
 */
export function mergeWorkspacePersist(
  persistedState: unknown,
  currentState: WorkspaceStore,
): WorkspaceStore {
  const persisted = (persistedState ?? {}) as Partial<PersistedWorkspaceStateV2>;
  const selection = persisted.selection ?? null;
  return {
    ...currentState,
    workspaces: persisted.workspaces ?? currentState.workspaces,
    userProfile: persisted.userProfile ?? currentState.userProfile,
    selection,
    currentWorkspace: projectCurrentWorkspace(selection),
  };
}

/** v1 shape: { currentWorkspace, workspaces, userProfile, isLoading } (no selection). */
interface PersistedWorkspaceStateV1 {
  currentWorkspace?: Workspace | null;
  workspaces?: Workspace[];
  userProfile?: UserProfile | null;
}

/**
 * v1 → v2 migration. The workspace selection carries over directly; a v1 null
 * selection falls back to the retired tenant-storage key — that pairing is
 * exactly what the old two-store state meant, so it seeds the explicit
 * unlinked-tenant kind rather than resurrecting a hidden second store.
 */
export function migrateWorkspacePersist(
  persistedState: unknown,
  version: number,
): PersistedWorkspaceStateV2 {
  if (version >= 2) {
    return persistedState as PersistedWorkspaceStateV2;
  }

  const v1 = (persistedState ?? {}) as PersistedWorkspaceStateV1;
  let selection: ClientSelection = null;
  if (v1.currentWorkspace?.id) {
    selection = { kind: 'workspace', workspace: v1.currentWorkspace };
  } else {
    const legacyTenant = readLegacyTenantWebsite();
    if (legacyTenant) {
      selection = { kind: 'tenant', tenantId: legacyTenant };
    }
  }
  clearLegacyTenantStorage();

  return {
    selection,
    workspaces: v1.workspaces ?? [],
    userProfile: v1.userProfile ?? null,
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/**
 * Collapse workspaces that share a website to one row.
 *
 * The backend can return two workspace rows for the same site (e.g. aaafence.com
 * was onboarded twice), which would show up twice in the picker. Dedupe once at
 * ingestion so every consumer — the picker, ProtectedRoute's id-then-website
 * reconciliation, the tenant bridge — sees a single canonical row per website.
 * Prefer an active row, then one that has Google Ads configured, then the first.
 */
function dedupeByWebsite(workspaces: Workspace[]): Workspace[] {
  const byWebsite = new Map<string, Workspace>();
  const passthrough: Workspace[] = []; // rows with no website can't collide
  for (const ws of workspaces) {
    const key = ws.website?.trim().toLowerCase();
    if (!key) {
      passthrough.push(ws);
      continue;
    }
    const existing = byWebsite.get(key);
    if (!existing || isPreferredWorkspace(ws, existing)) {
      byWebsite.set(key, ws);
    }
  }
  return [...byWebsite.values(), ...passthrough];
}

function isPreferredWorkspace(candidate: Workspace, current: Workspace): boolean {
  if (candidate.is_active !== current.is_active) return candidate.is_active;
  if (Boolean(candidate.has_google_ads) !== Boolean(current.has_google_ads)) {
    return Boolean(candidate.has_google_ads);
  }
  return false; // keep the first seen
}

function normalizeWebsite(website: string | null | undefined): string | null {
  const normalized = website?.trim().toLowerCase();
  return normalized ? normalized : null;
}

interface WorkspaceStore {
  // State
  workspaces: Workspace[];
  /** THE active-client context. The only stored selection value. */
  selection: ClientSelection;
  /**
   * Read-only projection of `selection` for legacy readers. Written atomically
   * with `selection` by the actions below; never set it directly.
   */
  currentWorkspace: Workspace | null;
  userProfile: UserProfile | null;
  isLoading: boolean;

  // Actions
  setWorkspaces: (workspaces: Workspace[]) => void;
  /** Select a workspace client (or clear with null). The normal path. */
  selectWorkspace: (workspace: Workspace | null) => void;
  /**
   * Select by tenant domain. Resolves to the matching workspace when one is
   * in the loaded list (trim/case-insensitive); otherwise selects the explicit
   * unlinked-tenant kind — the honest form of today's tenant-only deep links.
   */
  selectTenantByWebsite: (website: string) => void;
  /**
   * Clear the selection (sign-out). Writes null through persist so BOTH the
   * session value and the localStorage mirror are cleared — persist's own
   * removeItem keeps the mirror, which would re-seed a stale selection into
   * fresh tabs, so never use persist.clearStorage() for sign-out.
   */
  clearSelection: () => void;
  /**
   * @deprecated Compat alias for selectWorkspace while existing callers
   * (ProtectedRoute, WorkspaceSwitcher, invite/create flows) migrate. New code
   * uses selectWorkspace / selectTenantByWebsite.
   */
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  setUserProfile: (profile: UserProfile | null) => void;
  setIsLoading: (loading: boolean) => void;
  /**
   * Re-fetch the workspace list from the server.
   *
   * Workspaces are loaded exactly once per session by ProtectedRoute, and each
   * one carries `has_google_ads` — a flag the SERVER derives from whether the
   * tenant has a valid customer id. Anything that changes that id therefore
   * invalidates data nobody was refreshing: the operator connected an account,
   * saw "Connected", and the switcher still said "No Google Ads" until they
   * signed out (Art, 2026-07-21).
   *
   * Best-effort: a failed refresh must never break the save that triggered it.
   */
  refreshWorkspaces: () => Promise<void>;
  reset: () => void;
}

export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set, get) => ({
      workspaces: [],
      selection: null,
      currentWorkspace: null,
      userProfile: null,
      isLoading: false,

      setWorkspaces: (workspaces) => set({ workspaces: dedupeByWebsite(workspaces) }),

      selectWorkspace: (workspace) =>
        set({
          selection: workspace ? { kind: 'workspace', workspace } : null,
          currentWorkspace: workspace,
        }),

      selectTenantByWebsite: (website) => {
        const wanted = normalizeWebsite(website);
        if (!wanted) {
          get().clearSelection();
          return;
        }
        const match = get().workspaces.find(
          (ws) => normalizeWebsite(ws.website) === wanted,
        );
        if (match) {
          get().selectWorkspace(match);
          return;
        }
        set({ selection: { kind: 'tenant', tenantId: website }, currentWorkspace: null });
      },

      clearSelection: () => set({ selection: null, currentWorkspace: null }),

      setCurrentWorkspace: (workspace) => get().selectWorkspace(workspace),

      setUserProfile: (profile) => set({ userProfile: profile }),

      setIsLoading: (loading) => set({ isLoading: loading }),

      refreshWorkspaces: async () => {
        try {
          const workspaces = await fetchWorkspaces();
          const deduped = dedupeByWebsite(workspaces);
          const { selection } = get();
          // Keep the selected snapshot honest too: the refresh exists because
          // a derived flag on the list went stale, and the selected row is a
          // copy of that same list.
          if (selection?.kind === 'workspace') {
            const fresh = deduped.find((ws) => ws.id === selection.workspace.id);
            if (fresh) {
              set({
                workspaces: deduped,
                selection: { kind: 'workspace', workspace: fresh },
                currentWorkspace: fresh,
              });
              return;
            }
          }
          set({ workspaces: deduped });
        } catch {
          // Swallowed on purpose. This is called AFTER a successful save; the
          // save is what the user cares about, and surfacing a second error
          // for a background staleness refresh would be noise. Worst case the
          // badge stays stale until the next load, which is today's behaviour.
        }
      },

      reset: () =>
        set({
          workspaces: [],
          selection: null,
          currentWorkspace: null,
          userProfile: null,
          isLoading: false,
        }),
    }),
    {
      name: 'workspace-storage',
      version: 2,
      // Per-tab selection (sessionStorage) so multiple tabs can hold different
      // workspaces at once; localStorage mirror seeds new tabs. See perTabStorage.
      storage: createJSONStorage(perTabStorage),
      // One value is the selection; the list and profile ride along for instant
      // paint exactly as before. currentWorkspace is NOT persisted — it is a
      // projection, recomputed from the selection at merge time.
      partialize: (state) => ({
        selection: state.selection,
        workspaces: state.workspaces,
        userProfile: state.userProfile,
      }),
      migrate: migrateWorkspacePersist,
      merge: mergeWorkspacePersist,
    },
  ),
);
